import SearchBox from "@/common/SearchBox";
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useContractHistoryForCompany } from "~/services/common/contractHistory";
import { useBusinessMypageCompanyInfo } from "~/services/business/myPage";
import type { ContractHistoryItem } from "~/types/common";

export const Route = createFileRoute(
  "/business/layout/user-contract-management",
)({
  component: RouteComponent,
});

/* ------------------------------------------------------------------ helpers */

const formatDate = (iso: string | null): string => {
  if (!iso) return "-";
  // BE 에서 LocalDateTime 직렬화 → "yyyy-MM-ddTHH:mm:ss" 또는 배열. 첫 10자만 사용.
  const str = typeof iso === "string" ? iso : String(iso);
  return str.replace("T", " ").slice(0, 10).replace(/-/g, ".");
};

const statusBadgeColor = (status: ContractHistoryItem["status"]): string => {
  switch (status) {
    case "COMPLETED":
      return "bg-green-50 text-green-700 border-green-200";
    case "PENDING":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "REJECTED":
      return "bg-red-50 text-red-700 border-red-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "CANCELED":
      return "bg-gray-100 text-gray-500 border-gray-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-200";
  }
};

/** 상세 페이지 경로 — source 에 따라 분기. 레거시는 기존 라우트 유지. */
const buildDetailHref = (item: ContractHistoryItem): string => {
  if (item.source === "LEGACY") {
    return `/business/layout/user-contract-detail?histContrSeq=${item.itemSeq}`;
  }
  // 신규 e-sign 상세 — 미구현 시 동일 라우트로 가되 requestSeq 파라미터 전달
  return `/business/layout/user-contract-detail?requestSeq=${item.itemSeq}&source=REQUEST`;
};

/* ------------------------------------------------------------------ component */

function RouteComponent() {
  const { history } = useRouter();
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);

  const { data: companyInfo } = useBusinessMypageCompanyInfo();
  const companySeq = companyInfo?.companySeq ?? 0;

  const { data: listResp } = useContractHistoryForCompany({
    companySeq,
    page: currentPage,
    size: pageSize,
  });

  // 클라이언트 측 키워드 필터 (보호자명/펫명)
  const items: ContractHistoryItem[] = useMemo(() => {
    const raw = listResp?.data?.content ?? [];
    if (!currentSearchKeyword) return raw;
    const kw = currentSearchKeyword.trim().toLowerCase();
    return raw.filter(
      (it) =>
        (it.userName ?? "").toLowerCase().includes(kw) ||
        (it.petName ?? "").toLowerCase().includes(kw),
    );
  }, [listResp, currentSearchKeyword]);

  const totalCount = listResp?.data?.totalElements ?? 0;

  const handleSearch = (keyword: string) => {
    setCurrentSearchKeyword(keyword);
    setCurrentPage(1);
  };

  return (
    <>
      <LayoutHeader title={"계약 관리"} hasBackBtn hasFootPrint={false} />
      <div className="flex-auto bg-[#F5F5F5] min-h-[87.8vh]">
        {/* 서치박스 */}
        <div className="mx-2">
          <div className="flex justify-end items-center px-2 py-3">
            <SearchBox
              searchText={searchKeyword}
              setSearchText={setSearchKeyword}
              onSearch={handleSearch}
              placeholder="반려동물 이름 또는 보호자 닉네임으로 검색"
            />
          </div>
        </div>

        {/* 총 계약 수 */}
        <div className="flex gap-2 items-center pl-1 mx-4 mb-2">
          <div className="text-[14px] font-semibold">총 {totalCount}건</div>
          <div className="text-[13px] text-[#999999]">|</div>
          <div className="text-[13px] text-[#999999]">
            기존 + 신규 e-sign 통합 내역
          </div>
        </div>

        {/* 헤더 */}
        <div className="grid grid-cols-12 gap-2 items-center mx-4 mb-2">
          <div className="col-span-3 text-[14px] text-[#666666] text-center">
            고객/반려동물
          </div>
          <div className="col-span-3 text-[13px] text-[#666666] text-center">
            계약서
          </div>
          <div className="col-span-3 text-[14px] text-[#666666] text-center">
            상태
          </div>
          <div className="col-span-3 text-[13px] text-[#666666] text-center">
            일자
          </div>
        </div>

        {/* 목록 */}
        {items.length === 0 && (
          <div className="mx-4 mt-10 text-center text-[13px] text-[#999999]">
            표시할 계약 내역이 없습니다.
          </div>
        )}

        {items.map((item) => (
          <div
            className="grid grid-cols-12 gap-2 items-center mx-4 mb-2 bg-white rounded-lg border border-[#e9e9e9] min-h-[56px] py-2 px-1 cursor-pointer hover:bg-[#fafafa]"
            key={`${item.source}-${item.itemSeq}`}
            onClick={() => history.push(buildDetailHref(item))}
          >
            <div className="col-span-3 text-center">
              <div className="text-[14px] text-[#333] truncate">
                {item.userName ?? "-"}
              </div>
              <div className="text-[12px] text-[#888] truncate">
                {item.petName ?? "-"}
              </div>
            </div>
            <div className="col-span-3 text-[12px] text-[#666] text-center truncate px-1">
              {item.contractName ?? "위탁계약서"}
            </div>
            <div className="col-span-3 text-center">
              <span
                className={`inline-block px-2 py-[2px] rounded-full text-[11px] border ${statusBadgeColor(item.status)}`}
              >
                {item.statusLabel}
              </span>
              {item.source === "LEGACY" && (
                <div className="text-[10px] text-[#bbb] mt-[2px]">기존</div>
              )}
            </div>
            <div className="col-span-3 text-[12px] text-[#666] text-center">
              {formatDate(item.dtSigned ?? item.dtSent ?? item.dtRegist)}
            </div>
          </div>
        ))}

        {/* 페이지네이션 — 단순 prev/next */}
        {(listResp?.data?.totalPages ?? 0) > 1 && (
          <div className="flex justify-center gap-3 mt-4 pb-6">
            <button
              type="button"
              className="px-3 py-1 text-[12px] border rounded disabled:opacity-40"
              disabled={listResp?.data?.first}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              이전
            </button>
            <span className="text-[12px] text-[#666] self-center">
              {(listResp?.data?.number ?? 0) + 1} /{" "}
              {listResp?.data?.totalPages ?? 1}
            </span>
            <button
              type="button"
              className="px-3 py-1 text-[12px] border rounded disabled:opacity-40"
              disabled={listResp?.data?.last}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </>
  );
}
